import type { EnemyDef } from '../data/enemies';
import {
  applyStatus,
  computeDamage,
  hasStatus,
  statusMul,
  tickStatuses,
  type DamageEvent,
  type StatusEffect,
} from '../sim/Combat';
import { WEAPON_DAMAGE_MULTIPLIER, type DamageType } from '../config/constants';
import type { PathCache } from '../sim/Path';

let nextId = 1;
export function nextEntityId(): number {
  return nextId++;
}

export class Enemy {
  id: number;
  def: EnemyDef;
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  dist = 0;
  lane = 0;
  /** Formation metadata used to render and collide compact squads. */
  formationSlot = 0;
  formationSize = 1;
  formationOffset = 0;
  route?: PathCache;
  x = 0;
  y = 0;
  angle = 0;
  alive = true;
  /** Prevent duplicate bounty/kill attribution while the scene removes us. */
  killRecorded = false;
  /** Last weapon/source that damaged this unit (used for DoT kill credit). */
  lastDamageSourceId = '';
  hitFlash = 0;
  statuses: StatusEffect[] = [];
  spawnTimer = 0;
  children = 0;
  phaseIndex = 0;
  stunT = 0;
  slowMul = 1;
  /** Timer for damage smoke puffs. */
  smokeTimer = 0;
  /** Cooldown for ranged suppression fire. */
  fireTimer = 0;
  bob = Math.random() * Math.PI * 2;

  constructor(def: EnemyDef, hpMul = 1, speedMul = 1, powerMul = 1) {
    this.id = nextEntityId();
    this.def = def;
    this.powerMul = powerMul;
    this.maxHp = Math.round(def.hp * hpMul * powerMul);
    this.hp = this.maxHp;
    this.maxShield = Math.round((def.shield ?? 0) * powerMul);
    this.shield = this.maxShield;
    this.speedMul = speedMul;
  }

  /** Difficulty-scoped weapon output multiplier supplied by BattleScene. */
  weaponDamageMul = 1;

  speedMul: number;
  /** Shared balance multiplier for durability, movement, and breach pressure. */
  powerMul: number;

  get speed(): number {
    let s = this.def.speed * this.speedMul * this.powerMul;
    if (hasStatus(this.statuses, 'SLOW')) {
      s *= Math.max(0, 1 - Math.min(0.95, statusMul(this.statuses, 'SLOW')));
    }
    if (this.stunT > 0) s = 0;
    return Math.max(0, s);
  }

  get armour(): number {
    const breakAmt = statusMul(this.statuses, 'ARMOUR_BREAK');
    return Math.max(0, this.def.armour * this.powerMul - breakAmt);
  }

  get isElite(): boolean {
    return !!this.def.elite;
  }

  get isBoss(): boolean {
    return !!this.def.boss;
  }

  takeDamage(ev: DamageEvent): number {
    if (!this.alive) return 0;
    this.lastDamageSourceId = ev.sourceId;
    let dmg = ev.final;
    // Shield absorbs energy-friendly
    if (this.shield > 0) {
      const absorb = Math.min(this.shield, dmg);
      this.shield -= absorb;
      dmg -= absorb;
      if (ev.damageType === 'ENERGY') {
        // energy overcharges shield pop
        this.shield -= 4;
      }
    }
    this.hp -= dmg;
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
    }
    return ev.final;
  }

  applyHit(
    base: number,
    type: DamageType,
    armourPen: number,
    critChance: number,
    sourceId: string,
    buffMul = 1,
  ): number {
    const ev = computeDamage(base, type, this.def, this.armour, armourPen, critChance, buffMul, this.weaponDamageMul);
    ev.sourceId = sourceId;
    ev.targetId = String(this.id);
    this.hitFlash = 0.12;
    return this.takeDamage(ev);
  }

  applyStatuses(list: StatusEffect[]): void {
    for (const s of list) applyStatus(this.statuses, { ...s });
  }

  update(dt: number, pathLen: number, onSpawnChild?: (enemy: Enemy) => void): void {
    if (!this.alive) return;
    this.bob += dt * 4;
    if (this.hitFlash > 0) this.hitFlash -= dt;
    this.statuses = tickStatuses(this.statuses, dt);
    this.slowMul = 1;

    if (this.stunT > 0) this.stunT -= dt;

    // Burn DoT
    const burn = statusMul(this.statuses, 'BURN');
    if (burn > 0) {
      const burnSource = this.statuses.find((status) => status.kind === 'BURN')?.sourceId;
      if (burnSource) this.lastDamageSourceId = burnSource;
      // Burn is a weapon-applied damage-over-time effect, so it follows the
      // same global balance pass as direct projectile and ability hits.
      this.hp -= burn * WEAPON_DAMAGE_MULTIPLIER * this.weaponDamageMul * dt;
      if (this.hp <= 0) {
        this.hp = 0;
        this.alive = false;
        return;
      }
    }

    // Regen
    if (this.def.regen) this.hp = Math.min(this.maxHp, this.hp + this.def.regen * this.powerMul * dt);

    // Shield regen
    if (this.maxShield > 0 && this.def.shieldRegen) {
      this.shield = Math.min(this.maxShield, this.shield + this.def.shieldRegen * this.powerMul * dt);
    }

    // Brood spawn
    if (this.def.spawnChild && onSpawnChild && this.children < (this.def.spawnMax ?? 0)) {
      this.spawnTimer += dt;
      if (this.spawnTimer >= (this.def.spawnInterval ?? 3)) {
        this.spawnTimer = 0;
        this.children++;
        onSpawnChild(this);
      }
    }

    // Boss phases
    if (this.def.phases && this.phaseIndex < this.def.phases.length) {
      const pct = this.hp / this.maxHp;
      const phase = this.def.phases[this.phaseIndex];
      if (pct <= phase.hpPct) {
        this.phaseIndex++;
        this.speedMul *= phase.speedMul ?? 1;
        // phases handled by scene via event if needed
        this.pendingPhase = phase;
      }
    }

    const sp = this.speed;
    this.dist += sp * dt;
    if (this.dist >= pathLen) {
      this.dist = pathLen;
    }
  }

  pendingPhase: NonNullable<EnemyDef['phases']>[number] | null = null;

  get breached(): boolean {
    return this.alive && this.dist >= 0 && this.distDone;
  }

  distDone = false;

  markBreached(): void {
    this.distDone = true;
    this.alive = false;
  }
}
